<template>
  <div class="overflow-auto h-full">
    <div
      class="flex flex-wrap items-center justify-between my-4 lg:my-8 sticky top-0 z-20 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-gray-200 px-1 pt-4 pb-3 relative"
    >
      <!-- title -->
      <div class="order-1 flex-grow flex items-center text-navy pr-20 md:pr-0 mb-3 md:mb-0">
        <svg-icon type="mdi" size="32" :path="icons.users"> </svg-icon>

        <span class="ml-2 font-bold leading-7 text-2xl"> Users </span>
      </div>

      <!-- Create User button (mobile absolute) -->
      <div class="order-2 sm:order-3 sm:mr-3 absolute top-3 right-3 md:static">
        <inertia-link
          class="flex items-center justify-center w-7 h-7 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          :href="route('app.user.create')"
        >
          <svg-icon
            type="mdi"
            size="24"
            :path="icons.plus"
            class="text-white"
          />

          <span class="sr-only"> Add User </span>
        </inertia-link>
      </div>
      <!-- search (full-width mobile) -->
      <div class="order-3 w-full mt-3 md:mt-0 md:flex-1 md:order-2 md:mr-3">
        <search-component
          variant="toolbar"
          width="xl"
          :show-full-word-toggle="false"
          :form="form"
          :url="tabUrl(tab, false)"
          class="w-full"
        />
      </div>
    </div>

    <flash-message />

    <!-- Users belonging to the organisation, and farm owners, are separate lists -->
    <div class="border-b border-gray-200 mb-3">
      <nav class="-mb-px flex gap-6" aria-label="User type">
        <inertia-link
          :href="tabUrl('users')"
          :class="tabClass('users')"
        >
          Users
        </inertia-link>
        <inertia-link
          :href="tabUrl('farmers')"
          :class="tabClass('farmers')"
        >
          Farmers
          <span
            v-if="farmerCount"
            class="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
          >
            {{ farmerCount }}
          </span>
        </inertia-link>
      </nav>
    </div>

    <!-- Mobile Card List -->
    <div v-if="users.length > 0" class="space-y-3 md:hidden mt-2 pb-4">
      <div v-for="(user, index) in users" :key="'user-card-'+index" class="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex flex-col gap-2">
        <div class="flex justify-between items-start gap-3">
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold text-navy truncate" :title="user.name">{{ user.name }}</h3>
            <p class="text-[11px] text-gray-500 truncate" :title="user.email">{{ user.email }}</p>
          </div>
          <div class="shrink-0">
            <index-menu-item :links="indexLinks(user)" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3 text-[11px] leading-tight text-gray-700">
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Role</p>
            <div class="font-semibold capitalize" :title="user.role">{{ user.role }}</div>
          </div>
          <div v-if="user.enabled !== undefined">
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Status</p>
            <div :class="user.enabled ? 'text-green-600 font-medium' : 'text-red-600 font-medium'">{{ user.enabled ? 'Enabled' : 'Disabled' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table View (Desktop) -->
    <div v-if="users.length > 0" class="hidden md:block">
      <div class="mt-2 pb-14 xs:!w-full overflow-auto">
        <table
          class="table-auto min-w-[700px] xs:!mr-0 w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
        >
          <thead>
            <tr class="bg-gray-200 font-bold text-sm text-gray-900">
              <td class="px-2 py-2.5 whitespace-nowrap">Name</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Email</td>
              <td class="px-2 py-2.5 whitespace-nowrap">User Type</td>
              <td class="px-2 py-2.5 w-10"></td>
            </tr>
          </thead>

          <tbody class="bg-white">
            <tr
              class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
              v-for="(user, index) in users"
              :key="index"
            >
              <td class="px-2 py-2.5 whitespace-nowrap truncate" :title="user.name">
                {{ user.name }}
              </td>

              <td class="px-2 py-2.5 whitespace-nowrap truncate" :title="user.email">
                {{ user.email }}
              </td>

              <td class="px-2 py-2.5 whitespace-nowrap truncate" :title="user.role">
                {{ user.role }}
              </td>

              <td class="px-2 py-2.5 w-10 text-right">
                <index-menu-item :links="indexLinks(user)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-center">
        <pagination class="mt-8 mb-4" :links="links" />
      </div>
    </div>

    <div v-if="users.length === 0" class="bg-white mt-8 px-6 py-4">
      <strong>No users found.</strong>
    </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Pagination from "@/Components/Pagination";
import IndexMenuItem from "@/Components/IndexMenuItem";
import SearchComponent from "@/Components/SearchComponent";
import _ from "lodash";
import { mdiAccountGroupOutline, mdiPlus } from "@mdi/js";

export default {
  name: "usersManager",
  layout: [AppLayout],
  components: {
    Pagination,
    IndexMenuItem,
    SearchComponent,
  },

  props: {
    search: String,
    results: Object,
    paginate: String,
    tab: String,
    farmerCount: Number,
  },

  data() {
    return {
      form: {
        search: this.search,
      },
      users: this.results["original"]["data"],
      links: this.results["original"]["meta"]["pagination"],
      link: this.paginate,
      icons: {
        users: mdiAccountGroupOutline,
        plus: mdiPlus,
      },
    };
  },

  methods: {
    // one place builds the query for both tabs and for the search box, so the
    // two links cannot drift apart
    tabUrl(name, withSearch = true) {
      return route("app.user.index", {
        ...(name === "farmers" ? { tab: "farmers" } : {}),
        ...(withSearch && this.search ? { search: this.search } : {}),
      });
    },

    tabClass(name) {
      const active = (this.tab || 'users') === name;

      return [
        'whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium',
        active
          ? 'border-navy text-navy'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
      ];
    },

    toggleEnabled(id, index) {
      let self = this;
      axios
        .put(route("app.user.toggle-enabled", id))
        .then(function (response) {
          self.users[index].enabled = response.data.enabled;
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    indexLinks(user) {
      let links = {
        Edit: route("app.user.edit", user.id),
      };

      if (this.$page.props.auth.user.role == "admin" && user.can_assume) {
        links["Login As"] = route("login-as.assume", user.id);
      }

      return links;
    },
  },
};
</script>
